import config from '@wandb/common/config';
import {GraphQLError} from 'graphql/error';
import _, {compact} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  CloudProvider,
  ErrorSeverity,
  StorageBucketInfo,
  useAvailableBucketProvidersQuery,
  useOrganizationStorageBucketInfosQuery,
  useStorageBucketInfosQuery,
  useTestBucketStoreConnectionMutation,
} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {useDebounceState} from '../../util/hooks';
import {StorageBucketInfoFormValue, StorageBucketValidState} from './types';

export const cloudProviderValuesToDisplayNames = {
  [CloudProvider.Aws]: 'AWS',
  [CloudProvider.Azure]: 'Azure',
  [CloudProvider.Gcp]: 'Google',
  [CloudProvider.Minio]: 'Other S3-compatible storage',
};

export interface AzureName {
  accountName: string;
  containerName: string;
}
export function getAzureName(
  bucketInfo?: StorageBucketInfoFormValue
): AzureName | null {
  if (!bucketInfo || bucketInfo.provider !== CloudProvider.Azure) {
    return null;
  }

  const sections = bucketInfo.name.split('/');

  return {
    accountName: sections[0] ?? '',
    containerName: sections[1] ?? '',
  };
}

export function useCloudProviderOptions(): [
  isLoading: boolean,
  options: Array<{
    value: CloudProvider;
    text: string;
  }>,
  refetch: () => void
] {
  const {data, loading, refetch} = useAvailableBucketProvidersQuery();

  return useMemo(() => {
    if (loading) {
      return [true, [], refetch];
    }

    const options = (data?.serverInfo?.availableBucketProviders || []).map(
      provider => {
        return {
          value: provider,
          text: cloudProviderValuesToDisplayNames[provider],
        };
      }
    );

    return [false, options, refetch];
  }, [data, loading, refetch]);
}

export function useStorageBucketInfoOptions(
  organizationId: string | null
): [isLoading: boolean, options: StorageBucketInfo[], refetch: () => void] {
  const baseOptions = {
    context: propagateErrorsContext(),
    onError: ({
      graphQLErrors,
    }: {
      graphQLErrors: ReadonlyArray<GraphQLError>;
    }) => {
      graphQLErrors = graphQLErrors.filter(
        err => err.message !== 'permission denied'
      );
      if (graphQLErrors.length > 0) {
        throw graphQLErrors[0];
      }
    },
  };
  const storageBucketInfosQueryResult = useStorageBucketInfosQuery({
    // only execute this query in W&B server. In SaaS, we query for storage buckets at the organization level.
    // unfortunately, W&B server does not have the concept of organizations, so we have to make an entirely different query
    skip: !config.ENVIRONMENT_IS_PRIVATE,
    ...baseOptions,
  });
  const organizationStorageBucketInfosQueryResult =
    useOrganizationStorageBucketInfosQuery({
      variables: {
        organizationId: organizationId ?? '',
      },
      skip: organizationId == null,
      ...baseOptions,
    });

  return useMemo(() => {
    if (!config.ENVIRONMENT_IS_PRIVATE) {
      const {data, loading, refetch} =
        organizationStorageBucketInfosQueryResult;
      if (loading) {
        return [true, [], refetch];
      }
      const options =
        compact(
          data?.organization?.teams.map(team => team.settings.storageBucketInfo)
        ) ?? [];
      const uniqueOptions = _.uniqBy(options, o => o.ID);

      return [false, uniqueOptions, refetch];
    } else {
      const {data, loading, refetch} = storageBucketInfosQueryResult;
      if (loading) {
        return [true, [], refetch];
      }
      const options =
        compact(
          data?.entities?.edges.map(
            edge => edge.node?.settings.storageBucketInfo
          )
        ) ?? [];
      const uniqueOptions = _.uniqBy(options, o => o.ID);

      return [false, uniqueOptions, refetch];
    }
  }, [
    organizationStorageBucketInfosQueryResult,
    storageBucketInfosQueryResult,
  ]);
}

export interface StorageBucketInfoProps {
  /**
   * If this is false, the rest of the storage bucket input form will be hidden.
   */
  usingExternalStorage: boolean;
  setUsingExternalStorage: (val: boolean) => void;

  /**
   * Whether to enable the external storage option
   */
  isExternalStorageEnabled: boolean;
  setIsExternalStorageEnabled: (val: boolean) => void;

  formIsReady: boolean;

  /**
   * The bucket info seen by the parent -- might be an existing bucket (with an ID)
   * or a new bucket (without one)
   */
  bucketInfo: StorageBucketInfoFormValue;
  setBucketInfo: (val: StorageBucketInfoFormValue) => void;
  getInitialBucketInfo: () => NonNullable<StorageBucketInfoFormValue>;

  cloudProviderOptions: Array<{
    value: CloudProvider;
    text: string;
  }>;
  storageBucketInfoOptions: StorageBucketInfo[];

  isValidState: StorageBucketValidState;

  resetBucketInfo: () => void;

  /**
   * Overrides for storage option elements
   */
  storageLabelOptions?: React.ReactNode;
}

function useBucketValidState(
  bucketInfo: StorageBucketInfoFormValue,
  debouncedBucketInfo: StorageBucketInfoFormValue,
  organizationId: string | null
): StorageBucketValidState {
  const [valid, setValid] = useState<StorageBucketValidState>({state: 'unset'});

  const latestBucketInfo = useRef<StorageBucketInfoFormValue>(bucketInfo);
  const [testBucketStoreConnectionMutation] =
    useTestBucketStoreConnectionMutation();

  useEffect(() => {
    // we need this in a ref so we can check it in a callback *without*
    // invalidating the callback on changes
    latestBucketInfo.current = bucketInfo;
  }, [bucketInfo]);

  useEffect(() => {
    (async (validatingForBucketInfo: StorageBucketInfoFormValue) => {
      if (!validatingForBucketInfo || validatingForBucketInfo.name === '') {
        return;
      }

      setValid({state: 'loading'});

      const {data} = await testBucketStoreConnectionMutation({
        variables: {
          input: {
            ...validatingForBucketInfo,
            organizationID: organizationId,
          },
        },
      });

      // this is a guard against out-of-order responses when rapidly changing bucketInfo
      const dataRepresentsCurrentBucketInfo =
        latestBucketInfo.current === validatingForBucketInfo;

      const hasErrorsOrWarnings =
        (data?.testBucketStoreConnection?.length ?? 0) > 0;

      if (dataRepresentsCurrentBucketInfo && hasErrorsOrWarnings) {
        const errors = data?.testBucketStoreConnection
          .filter(err => err.severity === ErrorSeverity.Error)
          .map(err => err.message);
        const warnings = data?.testBucketStoreConnection
          .filter(err => err.severity === ErrorSeverity.Warn)
          .map(err => err.message);
        const newValidState: StorageBucketValidState = {
          state: (errors?.length ?? 0) > 0 ? 'invalid' : 'valid',
          errors,
          warnings,
        };
        setValid(newValidState);
        return;
      } else if (dataRepresentsCurrentBucketInfo) {
        setValid({
          state: 'valid',
        });
      }
    })(debouncedBucketInfo);
  }, [debouncedBucketInfo, testBucketStoreConnectionMutation, organizationId]);

  if (bucketInfo !== debouncedBucketInfo) {
    return {state: 'loading'};
  }

  return valid;
}

export function useStorageBucketConfig(
  organizationId: string | null
): StorageBucketInfoProps {
  const [usingExternalStorage, setUsingExternalStorage] = useState(false);
  const [externalStorageEnabled, setExternalStorageEnabled] = useState(false);

  const [
    bucketInfo,
    debouncedBucketInfo,
    setBucketInfo,
    setBucketInfoAndDebouncedBucketInfo,
  ] = useDebounceState<StorageBucketInfoFormValue>(null, 500);

  const [
    storageBucketInfoOptionsLoading,
    storageBucketInfoOptions,
    storageBucketInfoOptionsRefetch,
  ] = useStorageBucketInfoOptions(organizationId);

  const [
    cloudProviderOptionsLoading,
    cloudProviderOptions,
    cloudProviderOptionsRefetch,
  ] = useCloudProviderOptions();

  const formIsReady =
    !storageBucketInfoOptionsLoading && !cloudProviderOptionsLoading;

  const getInitialBucketInfo = useCallback(() => {
    if (storageBucketInfoOptions.length > 0) {
      setBucketInfoAndDebouncedBucketInfo(storageBucketInfoOptions[0]);
    }

    if (cloudProviderOptions.length < 1) {
      throw new Error(
        `No cloud providers available for BYOB. This is an invalid state; please contact support.`
      );
    }

    return {
      name: '',
      provider: cloudProviderOptions[0].value,
    };
  }, [
    cloudProviderOptions,
    setBucketInfoAndDebouncedBucketInfo,
    storageBucketInfoOptions,
  ]);

  // when the form has loaded, select the first available bucket if there
  // is one; otherwise populate a new bucket with an empty name and the
  // first available cloud provider
  useEffect(() => {
    if (!formIsReady || bucketInfo !== null) {
      return;
    }

    setBucketInfoAndDebouncedBucketInfo(getInitialBucketInfo());
  }, [
    bucketInfo,
    formIsReady,
    getInitialBucketInfo,
    setBucketInfoAndDebouncedBucketInfo,
  ]);

  const reset = useCallback(() => {
    setBucketInfoAndDebouncedBucketInfo(null);
    cloudProviderOptionsRefetch();
    storageBucketInfoOptionsRefetch();
  }, [
    cloudProviderOptionsRefetch,
    setBucketInfoAndDebouncedBucketInfo,
    storageBucketInfoOptionsRefetch,
  ]);

  const validState = useBucketValidState(
    bucketInfo,
    debouncedBucketInfo,
    organizationId
  );

  return {
    usingExternalStorage,
    setUsingExternalStorage,

    isExternalStorageEnabled: externalStorageEnabled,
    setIsExternalStorageEnabled: setExternalStorageEnabled,

    formIsReady,

    bucketInfo: usingExternalStorage ? bucketInfo : null,
    setBucketInfo,
    getInitialBucketInfo,

    cloudProviderOptions,
    storageBucketInfoOptions,

    isValidState: validState,

    resetBucketInfo: reset,
  };
}
